<template>
  <div class="timeline-wrapper" :style="{ display: selectedDecade === 0 && !timelineTransitioning ? 'none' : 'block' }">
    <div class="timeline-transition">
      <h3>{{ selectedDecade + 's' }}</h3>
    </div>

    <nav class="timeline-nav" :class="{ 'visible': !decadeLoading }">
  <a href="#item-previous" class="nav-item nav-item-previous" @click.prevent="handleAnchorClick('anchor-previous')"></a>
  <ul class="nav-items">
    <li v-for="item in items" :key="item.ID">
      <a :href="'#' + item.ID" @click.prevent="handleAnchorClick(item.ID)" class="nav-item nav-item-item">{{ item.the_title }}</a>
    </li>
    <div class="nav-item nav-item-active-indicator" :class="{ 'has-transition': !decadeLoading }" :style="{ top: timelineIndicatorTop }"></div>
  </ul>
  <a href="#item-next" class="nav-item nav-item-next" @click.prevent="handleAnchorClick('anchor-next')"></a>
</nav>

    <a href="#" class="timeline-close" :class="{ 'visible': !decadeLoading }" @click.prevent="selectDecade(0)"></a>

    <section ref="timeline" class="timeline" tabindex=0 @wheel.prevent="handleWheel" @keydown.down="handleAnchorClick('anchor-next')" @keydown.up="handleAnchorClick('anchor-previous')" @keydown.esc="selectDecade(0)" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
      <div class="timeline-left" :class="{ 'has-transition': decadeLoading }" :style="timelineLeftStyles">
        <div class="timeline-left-items-wrapper" :class="{ 'has-transition': !decadeLoading }" :style="timelineLeftItemsStyles">
          <div v-for="(item, index) in items" class="timeline-image" :key="item.ID" :style="getItemStyle(item, index)" @mousemove="handleImageMouseMove" @mouseleave="handleImageMouseLeave"></div>
        </div>
      </div>
      <div class="timeline-right" :class="{ 'has-transition': decadeLoading }" :style="timelineRightStyles">
        <div class="timeline-right-items-wrapper" :class="{ 'has-transition': !decadeLoading }" :style="timelineRightItemsStyles">
          <div class="timeline-right-item" v-for="item in items" :key="item.ID">
            <div class="timeline-copy">
              <h3>{{ item.year }}</h3>
              <h2 v-html="item.the_title"></h2>
              <div v-html="item.the_content"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';

export default {
  mounted() {
    this.mouseAnimationFrame = null;
    this.touchClientY = 0;
    this.touchStarted = false;
  },
  computed: {
    ...mapGetters([
      'selectedDecade',
      'items',
      'numItems',
      'currentAnchorIndex',
      'scrollThreshold',
      'scrolling',
      'decadeLoading',
      'decadeTransitioning',
      'timelineTransitioning',
    ]),
    timelineLeftStyles() {
      return {
        height: this.numItems * 100 + '%',
        transform: `translateX(${this.timelineLeftTranslateX})`,
      };
    },
    timelineRightStyles() {
      return {
        height: this.numItems * 100 + '%',
        transform: `translateX(${this.timelineRightTranslateX})`,
      };
    },
    timelineLeftItemsStyles() {
      return {
        transform: `translateY(${this.timelineLeftTranslateY})`,
      };
    },
    timelineRightItemsStyles() {
      return {
        transform: `translateY(${this.timelineRightTranslateY})`,
      };
    },
    timelineLeftTranslateX() {
      return this.decadeLoading ? '-100%' : '0%';
    },
    timelineLeftTranslateY() {
      const translatePerItem = 100 / this.numItems;
      return this.decadeTransitioning ? '-100%' : `${translatePerItem * (this.numItems - (this.currentAnchorIndex + 1)) * -1}%`;
    },
    timelineRightTranslateX() {
      return this.decadeLoading ? '100%' : '0%';
    },
    timelineRightTranslateY() {
      const translatePerItem = 100 / this.numItems;
      return this.decadeTransitioning ? `${translatePerItem}%` : `${translatePerItem * this.currentAnchorIndex * -1}%`;
    },
    timelineIndicatorTop() {
      return this.currentAnchorIndex * 29 + 'px';
    },
    backgroundPosition() {
      return this.backgroundPositionX + '% ' + this.backgroundPositionY + '%';
    },
  },
  methods: {
    ...mapActions(['setCurrentAnchorIndex', 'setScrollThreshold', 'selectDecade']),
    handleAnchorClick(anchor) {
      let index;
      if (anchor === 'anchor-next') {
        index = this.currentAnchorIndex + 1;
      } else if (anchor === 'anchor-previous') {
        index = this.currentAnchorIndex - 1;
      } else {
        index = this.items.findIndex(item => item.ID === parseInt(anchor));
      }
      if (index >= 0 && index < this.numItems) {
        this.setCurrentAnchorIndex(index);
        this.setScrollThreshold(0);
      }
    },
    handleScroll(deltaY) {
      const scrollThreshold = 50;
      if (!this.scrolling) {
        this.setScrollThreshold(this.scrollThreshold + deltaY);
        if (this.scrollThreshold > scrollThreshold) {
          this.touchStarted = false;
          if (this.currentAnchorIndex < this.numItems - 1) {
            this.setCurrentAnchorIndex(this.currentAnchorIndex + 1);
            this.setScrollThreshold(0);
          } else {
            this.setScrollThreshold(scrollThreshold);
          }
        } else if (this.scrollThreshold < -scrollThreshold) {
          this.touchStarted = false;
          if (this.currentAnchorIndex > 0) {
            this.setCurrentAnchorIndex(this.currentAnchorIndex - 1);
            this.setScrollThreshold(0);
          } else {
            this.setScrollThreshold(-scrollThreshold);
          }
        }
      }
    },
    handleWheel: _.throttle(function (e) {
      this.handleScroll(e.deltaY);
    }, 50),
    handleTouchStart(e) {
      if (this.selectedDecade === 0) return;
      this.touchStarted = true;
      this.touchClientY = e.touches[0].clientY;
    },
    handleTouchMove(e) {
      if (this.selectedDecade === 0 || !this.touchStarted) return;
      const deltaY = (e.changedTouches[0].clientY - this.touchClientY) * -1;
      this.touchClientY = e.changedTouches[0].clientY;
      this.handleScroll(deltaY);
    },
    animateMousePosition(target, mouseX, mouseY) {
      const deadZoneX = target.clientWidth * 0.1;
      const mousePercentageX = ((mouseX - deadZoneX) / (target.clientWidth - deadZoneX * 2)) * 100;
      const frame = (mousePercentageX - this.backgroundPositionX) / (target.clientWidth / 60);
      cancelAnimationFrame(this.mouseAnimationFrame);
      const animate = () => {
        this.backgroundPositionX = Math.max(Math.min(this.backgroundPositionX + frame, 100), 0);
        if ((frame < 0 && this.backgroundPositionX > mousePercentageX) || (frame > 0 && this.backgroundPositionX < mousePercentageX)) {
          this.mouseAnimationFrame = requestAnimationFrame(animate);
        }
      };
      this.mouseAnimationFrame = requestAnimationFrame(animate);
    },
    handleImageMouseLeave(e) {
      cancelAnimationFrame(this.mouseAnimationFrame);
      this.animateMousePosition(e.target, e.target.clientWidth / 2, e.target.clientHeight / 2);
    },
    handleImageMouseMove: _.throttle(function (e) {
      this.animateMousePosition(e.target, e.offsetX, e.offsetY);
    }, 10),
    getItemStyle(item, index) {
      return {
        backgroundImage: `url('${item.main_image}')`,
        height: 100 / this.numItems + '%',
        backgroundPosition: this.currentAnchorIndex === index ? this.backgroundPosition : '50% 50%',
      };
    },
  },
  data() {
    return {
      backgroundPositionX: 50,
      backgroundPositionY: 50,
    };
  },
  watch: {
    decadeTransitioning(val) {
      if (!val && this.selectedDecade !== 0) {
        this.$refs.timeline.focus();
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// Timeline.vue?7fd70d21